import { describeError, useApi } from '@classgo/app-kit';
import type { StudentListItem } from '@classgo/shared';
import { Badge, Button, Card, EmptyState, Input, Modal, Select, Table } from '@classgo/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { listQuery } from '../../academic/api';
import { StudentForm } from '../../forms/student-form';
import { useT } from '../../i18n';
import { recordQueries } from '../../records/api';
import { useTenant } from '../../tenant/tenant-provider';

/**
 * Status badge of a student.
 */
export const StudentStatus = ({ status }: { status: string }) => {
  const t = useT();
  const key =
    status === 'withdrawn' ? 'withdrawn' : status === 'graduated' ? 'graduated' : 'active';
  return <Badge tone={key === 'active' ? 'success' : 'gray'}>{t(`student.status.${key}`)}</Badge>;
};

/**
 * Students of the selected sede, filtered by grade, group or a search, paged by fifty.
 */
export const StudentsScreen = () => {
  const t = useT();
  const api = useApi();
  const navigate = useNavigate();
  const { session, sede } = useTenant();
  const [creating, setCreating] = useState(false);
  const canWrite = session.permissions.all || session.permissions.keys.includes('students:write');
  const [gradeId, setGradeId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const grades = useQuery(listQuery(api, 'grades'));
  const groups = useQuery(listQuery(api, 'groups', { sedeId: sede.id }));
  const students = useQuery(
    recordQueries.students(api, {
      sedeId: sede.id,
      ...(gradeId ? { gradeId } : {}),
      ...(groupId ? { groupId } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
      page,
    }),
  );
  const total = students.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / 50));
  const visibleGroups = (groups.data?.items ?? []).filter(
    (group) => !gradeId || group.gradeId === gradeId,
  );

  return (
    <Card
      title={t('students.title')}
      flush
      actions={
        canWrite ? (
          <Button onClick={() => setCreating(true)}>{t('students.new')}</Button>
        ) : undefined
      }
    >
      <Modal open={creating} onClose={() => setCreating(false)} title={t('students.new')}>
        {creating ? (
          <StudentForm
            current={null}
            onDone={(id) => {
              setCreating(false);
              void navigate({ to: '/estudiantes/$studentId', params: { studentId: id } });
            }}
          />
        ) : null}
      </Modal>
      <div className="mb-4 grid gap-3 px-4 pt-4 sm:grid-cols-3">
        <Select
          label={t('students.filter.grade')}
          value={gradeId}
          onChange={(event) => {
            setGradeId(event.target.value);
            setGroupId('');
            setPage(1);
          }}
          options={[
            { value: '', label: t('students.filter.all') },
            ...(grades.data?.items ?? []).map((grade) => ({ value: grade.id, label: grade.name })),
          ]}
        />
        <Select
          label={t('students.filter.group')}
          value={groupId}
          onChange={(event) => {
            setGroupId(event.target.value);
            setPage(1);
          }}
          options={[
            { value: '', label: t('students.filter.all') },
            ...visibleGroups.map((group) => ({ value: group.id, label: group.name })),
          ]}
        />
        <Input
          label={t('students.filter.search')}
          help={t('students.filter.search.help')}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
        />
      </div>
      {students.data?.schoolYear === null || students.data?.schoolYear?.status === 'planned' ? (
        <p
          role="note"
          className="mx-4 mb-4 rounded-control bg-warning-100 px-3 py-2 text-sm text-warning-700"
        >
          {students.data.schoolYear
            ? t('students.year.planned', { year: students.data.schoolYear.name })
            : t('students.year.none')}{' '}
          {students.data.schoolYear ? (
            <Link
              to="/anos/$yearId"
              params={{ yearId: students.data.schoolYear.id }}
              className="font-medium underline underline-offset-2 hover:decoration-2"
            >
              {t('students.year.activate', { year: students.data.schoolYear.name })}
            </Link>
          ) : (
            <Link
              to="/anos"
              className="font-medium underline underline-offset-2 hover:decoration-2"
            >
              {t('students.year.create')}
            </Link>
          )}
        </p>
      ) : null}
      {students.isError ? (
        <div className="p-6">
          <EmptyState
            title={t('entity.error.title')}
            description={describeError(t, students.error)}
            action={
              <Button variant="secondary" onClick={() => void students.refetch()}>
                {t('session.retry')}
              </Button>
            }
          />
        </div>
      ) : (
        <Table<StudentListItem>
          caption={t('students.caption')}
          rows={students.data?.items ?? []}
          rowKey={(row) => row.id}
          loading={students.isPending}
          onRowClick={(row) =>
            void navigate({ to: '/estudiantes/$studentId', params: { studentId: row.id } })
          }
          empty={
            <EmptyState title={t('students.empty.title')} description={t('students.empty.body')} />
          }
          columns={[
            {
              key: 'name',
              header: t('students.column.name'),
              cell: (row) => (
                <div>
                  <p className="font-medium text-gray-900">
                    {row.familyName}, {row.givenName}
                  </p>
                  <p className="text-xs text-gray-500">{row.email}</p>
                </div>
              ),
            },
            {
              key: 'document',
              header: t('students.column.document'),
              cell: (row) =>
                row.documentNumber ? `${row.documentType} ${row.documentNumber}` : t('common.none'),
            },
            {
              key: 'group',
              header: t('students.column.group'),
              cell: (row) => (row.groupName ? `${row.groupName}` : t('common.none')),
            },
            {
              key: 'status',
              header: t('students.column.status'),
              cell: (row) => <StudentStatus status={row.status} />,
            },
          ]}
        />
      )}
      <div className="flex flex-col gap-2 border-t border-gray-200 px-4 py-3 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between">
        <span>{t('students.total', { total })}</span>
        <div className="flex items-center gap-2">
          <span>{t('students.page', { page, pages })}</span>
          <Button
            size="sm"
            variant="secondary"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            {t('students.prev')}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={page >= pages}
            onClick={() => setPage(page + 1)}
          >
            {t('students.next')}
          </Button>
        </div>
      </div>
    </Card>
  );
};
